import React, { useState } from 'react';

export default function Contact() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || !message) return;
    
    // In a real-world scenario, this would push to an API or Firestore.
    // For our robust client-side suite, we present a clean, positive confirmation state.
    setSubmitted(true);
    setName('');
    setEmail('');
    setSubject('');
    setMessage('');
  };

  return (
    <div className="w-full max-w-4xl mx-auto flex flex-col gap-10">
      <div className="text-center max-w-lg mx-auto">
        <h2 className="text-4xl font-extrabold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent mb-3">
          Get in Touch
        </h2>
        <p className="text-gray-400 text-sm">
          Have questions, custom integration queries, or feature requests? Reach out to our client-side support crew.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
        {/* Left Side: Support Message & Details */}
        <div className="md:col-span-2 bg-gray-900/40 border border-gray-900 rounded-2xl p-6 flex flex-col justify-between gap-6 backdrop-blur-sm shadow-lg">
          <div className="flex flex-col gap-4">
            <h3 className="text-lg font-bold text-gray-100">Official Support</h3>
            <p className="text-gray-400 text-xs leading-relaxed">
              GetQRPost is built and maintained on client-first principles. We answer support queries, bug reports, and customization suggestions within 24 hours.
            </p>
            
            <div className="flex flex-col gap-3 mt-4">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-indigo-600/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wider">Email Address</span>
                  <a href="mailto:contact@getqrpost.com" className="text-sm font-semibold text-gray-200 hover:text-indigo-400 transition-colors">
                    contact@getqrpost.com
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-indigo-600/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wider">100% Confidential</span>
                  <span className="text-xs text-gray-400">
                    No data is ever logged or shared.
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-900 pt-4 text-[10px] uppercase font-bold text-emerald-400 tracking-wider flex items-center gap-1.5 justify-center md:justify-start">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
            Active Client Support Live
          </div>
        </div>

        {/* Right Side: Inquiry Form */}
        <div className="md:col-span-3 bg-gray-900/40 border border-gray-800 rounded-2xl p-6 backdrop-blur-sm shadow-xl">
          {submitted ? (
            <div className="h-full flex flex-col items-center justify-center py-12 text-center gap-4">
              <div className="h-12 w-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-100">Message Received!</h3>
              <p className="text-gray-400 text-sm max-w-sm">
                Thank you for contacting GetQRPost support. A member of our client care crew will get back to you shortly.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="mt-2 px-4 py-2 rounded-xl bg-gray-900 hover:bg-gray-800 border border-gray-800 text-xs font-bold text-gray-300 uppercase tracking-wider transition-all"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Full Name</label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your Name"
                    className="w-full bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-4 py-2.5 text-sm text-gray-100 placeholder-gray-600 outline-none transition-all duration-200"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Email Address</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-4 py-2.5 text-sm text-gray-100 placeholder-gray-600 outline-none transition-all duration-200"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Inquiry Topic"
                  className="w-full bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-4 py-2.5 text-sm text-gray-100 placeholder-gray-600 outline-none transition-all duration-200"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Message Content</label>
                <textarea
                  required
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Enter details..."
                  rows={4}
                  className="w-full bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-4 py-2.5 text-sm text-gray-100 placeholder-gray-600 outline-none transition-all duration-200 resize-none"
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 px-4 mt-2 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-bold text-sm shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/35 transition-all duration-200 active:scale-[0.98]"
              >
                Send Inquiry
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
